import React, { useEffect, useMemo, useState } from 'react'
import { Alert, Button, DatePicker, Modal, Progress, Table } from 'antd'
import moment from 'moment'
import {
  BULK_MARK_RECEIVED_LABEL,
  BULK_RESULT,
  summarizeBulkMarkReceivedResults,
} from 'src/utils/goodsReceiptingBulkHelpers'
import {
  ACTUAL_DELIVERY_DATE_FUTURE_MESSAGE,
  parseActualDeliveryDate,
  validateActualDeliveryDate,
} from 'src/utils/actualDeliveryDate'

const STEP = {
  CONFIRM: 'confirm',
  RUNNING: 'running',
  RESULTS: 'results',
}

/**
 * Confirmation → progress → results for bulk historical Full-accept.
 */
const BulkMarkReceivedModal = ({
  visible,
  orders = [],
  venueTitle = '',
  processing = false,
  progress = { done: 0, total: 0 },
  results = null,
  onCancel,
  onConfirm,
}) => {
  const [step, setStep] = useState(STEP.CONFIRM)
  const [deliveryDate, setDeliveryDate] = useState(null)
  const [dateError, setDateError] = useState('')

  useEffect(() => {
    if (!visible) {
      setStep(STEP.CONFIRM)
      setDeliveryDate(null)
      setDateError('')
      return
    }
    if (processing) {
      setStep(STEP.RUNNING)
      return
    }
    if (results) {
      setStep(STEP.RESULTS)
    }
  }, [visible, processing, results])

  const summary = useMemo(
    () => (results ? summarizeBulkMarkReceivedResults(results) : null),
    [results],
  )

  const skippedOrFailedRows = useMemo(() => {
    if (!results) return []
    return results.filter(item => item.code !== BULK_RESULT.SUCCESS)
  }, [results])

  const handleConfirm = () => {
    let stampYmd = null
    if (deliveryDate) {
      const check = validateActualDeliveryDate(deliveryDate, {
        required: false,
      })
      if (!check.ok) {
        setDateError(
          check.code === 'future'
            ? ACTUAL_DELIVERY_DATE_FUTURE_MESSAGE
            : check.message || 'Invalid delivery date.',
        )
        return
      }
      const parsed = parseActualDeliveryDate(deliveryDate)
      stampYmd = parsed.ok ? parsed.ymd : null
    }
    setDateError('')
    onConfirm({ actualDeliveryDate: stampYmd })
  }

  const percent =
    progress.total > 0
      ? Math.min(100, Math.round((progress.done / progress.total) * 100))
      : 0

  return (
    <Modal
      title={
        step === STEP.RESULTS
          ? 'Bulk Goods Receipting complete'
          : 'Mark historical orders as received?'
      }
      visible={visible}
      destroyOnClose
      maskClosable={step !== STEP.RUNNING}
      closable={step !== STEP.RUNNING}
      onCancel={() => {
        if (step === STEP.RUNNING) return
        onCancel()
      }}
      footer={
        step === STEP.CONFIRM
          ? [
              <Button key='cancel' onClick={onCancel}>
                Cancel
              </Button>,
              <Button
                key='ok'
                type='primary'
                onClick={handleConfirm}
                disabled={!orders.length}
              >
                {BULK_MARK_RECEIVED_LABEL}
              </Button>,
            ]
          : step === STEP.RUNNING
          ? [
              <Button key='working' disabled>
                Processing…
              </Button>,
            ]
          : [
              <Button key='done' type='primary' onClick={onCancel}>
                Done
              </Button>,
            ]
      }
      width={640}
    >
      {step === STEP.CONFIRM ? (
        <>
          <p>
            You are about to mark <strong>{orders.length}</strong> order
            {orders.length === 1 ? '' : 's'} as received
            {venueTitle ? (
              <>
                {' '}
                for <strong>{venueTitle}</strong>
              </>
            ) : null}
            .
          </p>
          <Alert
            type='info'
            showIcon
            style={{ marginBottom: 12 }}
            message='This will'
            description={
              <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
                <li>Update their Goods Receipting status (Full accept).</li>
                <li>
                  Remove successfully processed orders from the outstanding
                  receiving list.
                </li>
                <li>Record that they were processed using a bulk action.</li>
              </ul>
            }
          />
          <Alert
            type='warning'
            showIcon
            style={{ marginBottom: 12 }}
            message='This will not'
            description={
              <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
                <li>Reconcile invoices automatically.</li>
                <li>Upload or forward invoices.</li>
                <li>Change existing Dext records.</li>
                <li>Alter supplier payment or accounting records.</li>
              </ul>
            }
          />
          <Alert
            type='warning'
            showIcon
            style={{ marginBottom: 16 }}
            message='Only use bulk confirmation when you are satisfied these historical deliveries have been completed. Orders with short / problem lines are skipped for individual review.'
          />
          <div style={{ marginBottom: 8, fontWeight: 600 }}>
            Actual delivery date (optional)
          </div>
          <DatePicker
            style={{ width: '100%' }}
            value={deliveryDate}
            onChange={value => {
              setDeliveryDate(value)
              setDateError('')
            }}
            format='YYYY-MM-DD'
            disabledDate={current =>
              current && current.isAfter(moment(), 'day')
            }
            placeholder='Leave blank to keep each order’s existing date'
          />
          {dateError ? (
            <div style={{ color: '#cf1322', marginTop: 6 }}>{dateError}</div>
          ) : (
            <div style={{ color: '#666', marginTop: 6, fontSize: 12 }}>
              If set, this date is stamped on every successfully processed
              order. Otherwise each order keeps its existing actual delivery
              date when present — we do not invent quantities or dates.
            </div>
          )}
        </>
      ) : null}

      {step === STEP.RUNNING ? (
        <>
          <p>
            Processing {progress.done} of {progress.total}…
          </p>
          <Progress percent={percent} status='active' />
        </>
      ) : null}

      {step === STEP.RESULTS && summary ? (
        <>
          <Alert
            type='success'
            showIcon
            style={{ marginBottom: 12 }}
            message={`${summary.success} order${
              summary.success === 1 ? '' : 's'
            } successfully marked as received.`}
          />
          {summary.skippedAlreadyReceived > 0 ? (
            <p>
              {summary.skippedAlreadyReceived} order
              {summary.skippedAlreadyReceived === 1 ? '' : 's'} skipped because{' '}
              {summary.skippedAlreadyReceived === 1 ? 'it was' : 'they were'}{' '}
              already received.
            </p>
          ) : null}
          {summary.needsReview + summary.skippedCreditWorkflow > 0 ? (
            <p>
              {summary.needsReview + summary.skippedCreditWorkflow} order
              {summary.needsReview + summary.skippedCreditWorkflow === 1
                ? ''
                : 's'}{' '}
              require individual review.
            </p>
          ) : null}
          {summary.failed > 0 ? (
            <p style={{ color: '#cf1322' }}>
              {summary.failed} order{summary.failed === 1 ? '' : 's'} failed to
              save.
            </p>
          ) : null}
          {skippedOrFailedRows.length > 0 ? (
            <Table
              size='small'
              pagination={false}
              style={{ marginTop: 12 }}
              rowKey={row =>
                `${row.purchaseOrderNumber}-${row.code}-${row.reason}`
              }
              dataSource={skippedOrFailedRows}
              columns={[
                {
                  title: 'PO',
                  dataIndex: 'purchaseOrderNumber',
                  key: 'po',
                  width: 120,
                },
                {
                  title: 'Supplier',
                  dataIndex: 'supplierTitle',
                  key: 'supplier',
                },
                {
                  title: 'Reason',
                  dataIndex: 'reason',
                  key: 'reason',
                },
              ]}
            />
          ) : null}
        </>
      ) : null}
    </Modal>
  )
}

export default BulkMarkReceivedModal
